import {MessageCodes} from "../MessageCodes";
import {useAppContext} from "../../../context/AppContext";
import ResultView from "../../../components/ResultView";
import ResultText from "../ResultText";
import EmptyLine from "../EmptyLine";
import ResultTable from "../../../components/table/ResultTable";
import ResultHeader from "../../../components/table/ResultHeader";
import ResultBody from "../../../components/table/ResultBody";
import ResultRow from "../../../components/table/ResultRow";
import ResultHeaderCell from "../../../components/table/ResultHeaderCell";
import ResultBodyCell from "../../../components/table/ResultBodyCell";
import HecRasHelper from "../../../helpers/HecRasHelper";

// "CITY OF WINSTON-SALEM (375360)" — cid yoksa sadece ad.
const fmtCommunity = (a) => {
    if (a?.name && a?.cid) return `${a.name} (${a.cid})`
    return a?.name || (a?.cid ? `(${a.cid})` : '')
}

// C-OV4 "Revised/Affected Reach" — Comparison Overview.
// Reach aralıkları (revised/affected/project/bounding) calcWSEL ile FRONTEND'de
// türetilir (tüm comparison check'lerine bağlı, backend'e taşınamaz). Affected
// FIRM panel / community ise backend'in per-XS spatial haritasından (check-
// bağımsız) affected XS'lere göre süzülür.
export function COV4({message_key}) {
    const {getSelectedHistory} = useAppContext()
    const history = getSelectedHistory()
    const wsel = history?.wsel
    const summary = history?.summary

    if (!wsel) return null

    const minCS = wsel.minCS
    const maxCS = wsel.maxCS

    // RS modelin US/DS limitindeyse (1) dipnotu.
    const isLimit = (cs) => parseFloat(cs) === minCS || parseFloat(cs) === maxCS
    const toLimitStr = (cs) => isLimit(cs) ? (<>{parseFloat(cs)}<sup>(1)</sup></>) : cs

    // Affected reach aralığındaki XS'leri backend per-XS FIRM/community haritasıyla
    // eşleştir → benzersiz. (affected -1/-1 ise eşleşme olmaz; 0/0 ise tüm reach.)
    // affected_reach_min/max, findMinMax'ten ham XS "text"i olarak gelir; koprude
    // "37729   BR D" gibi son ek tasiyabilir. `>=`/`<=` degeri Number() ile zorlar
    // ve boyle bir string NaN olur (tum karsilastirmalar false) -> once parseFloat
    // ile sayisallastir. cross_section_value zaten sayisal (or. 38042).
    const affMin = parseFloat(wsel.affected_reach_min)
    const affMax = parseFloat(wsel.affected_reach_max)
    const inAffected = (v) => {
        if (v === null || v === undefined) return false
        if (Number.isNaN(affMin) || Number.isNaN(affMax)) return false
        if (affMin === 0 && affMax === 0) return true
        return v >= affMin && v <= affMax
    }

    const firmPerXs = summary?.base?.firm_panels_per_xs || []
    const politicalPerXs = summary?.base?.political_per_xs || []

    const affectedPanels = [...new Set(
        firmPerXs.filter(r => inAffected(r.cross_section_value)).map(r => r.panel).filter(Boolean)
    )].sort((a, b) => (a.length - b.length) || a.localeCompare(b))

    const seenC = new Set()
    const affectedCommunities = []
    politicalPerXs
        .filter(r => inAffected(r.cross_section_value))
        .forEach(r => {
            if (!(r.name || r.cid)) return
            const key = `${r.name}|${r.cid}`
            if (seenC.has(key)) return
            seenC.add(key)
            affectedCommunities.push({name: r.name, cid: r.cid})
        })

    const hasLimit = [
        wsel.revised_reach_min, wsel.revised_reach_max,
        wsel.affected_reach_min, wsel.affected_reach_max,
        wsel.project_min, wsel.project_max,
        wsel.boundary_min, wsel.boundary_max,
    ].some(isLimit)

    // Bir aralık satırı → US(min)/DS(max). min <= -1 ise "yok/identical" notu;
    // 0/0 (tüm reach) ise modelin US/DS uçlarını (minCS/maxCS) göster.
    const rangeRow = (label, min, max, emptyText) => {
        if (min <= -1) {
            return (
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left'}}>{label}</ResultHeaderCell>
                    <ResultBodyCell colSpan={2} style={{textAlign: 'center'}}>{emptyText}</ResultBodyCell>
                </ResultRow>
            )
        }
        const whole = (min === 0 && max === 0)
        const lo = whole ? minCS : min
        const hi = whole ? maxCS : max
        return (
            <ResultRow>
                <ResultHeaderCell style={{textAlign: 'left'}}>{label}</ResultHeaderCell>
                <ResultBodyCell style={{textAlign: 'right'}}>{toLimitStr(lo)}</ResultBodyCell>
                <ResultBodyCell style={{textAlign: 'right'}}>{toLimitStr(hi)}</ResultBodyCell>
            </ResultRow>
        )
    }

    return (
        <ResultView error_id={MessageCodes.Compare.COV04.error_id} message_key={message_key}>
            <ResultText>
                The revised and impacted reaches are identified as follows.
            </ResultText>
            <EmptyLine/>
            <ResultTable noDescription>
                <ResultHeader>
                    <ResultRow>
                        <ResultHeaderCell style={{textAlign: 'left', width: HecRasHelper.getWidth(30)}}>River Station</ResultHeaderCell>
                        <ResultHeaderCell style={{textAlign: 'right', width: HecRasHelper.getWidth(20)}}>US</ResultHeaderCell>
                        <ResultHeaderCell style={{textAlign: 'right', width: HecRasHelper.getWidth(20)}}>DS</ResultHeaderCell>
                    </ResultRow>
                </ResultHeader>
                <ResultBody>
                    {rangeRow('Revised Geometry Reach', wsel.revised_reach_min, wsel.revised_reach_max, 'Geometry is identical!')}
                    {rangeRow('Affected Reach', wsel.affected_reach_min, wsel.affected_reach_max, '100yr WSEL profile is identical!')}
                    {/* Aralık değil, tek değer/liste — US/DS sütunlarını birleştir
                        (colSpan=2), sağa yaslı. */}
                    {affectedPanels.length > 0 && (
                        <ResultRow>
                            <ResultHeaderCell style={{textAlign: 'left'}}>Affected FIRM Panel</ResultHeaderCell>
                            <ResultBodyCell colSpan={2} style={{textAlign: 'right'}}>{affectedPanels.join(', ')}</ResultBodyCell>
                        </ResultRow>
                    )}
                    {affectedCommunities.length > 0 && (
                        <ResultRow>
                            <ResultHeaderCell style={{textAlign: 'left'}}>Affected FIRM Community</ResultHeaderCell>
                            <ResultBodyCell colSpan={2} style={{textAlign: 'right'}}>{affectedCommunities.map(fmtCommunity).join(', ')}</ResultBodyCell>
                        </ResultRow>
                    )}
                    {rangeRow('Project Reach', wsel.project_min, wsel.project_max, 'Two plans are identical!')}
                    {rangeRow('Bounding (Tie-in) XS', wsel.boundary_min, wsel.boundary_max, 'None')}
                </ResultBody>
            </ResultTable>
            {hasLimit && (
                <ResultText>
                    <sup>(1)</sup> US/DS limit of the model.
                </ResultText>
            )}
        </ResultView>
    )
}
